import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useAtomValue } from "jotai";
import { ArrowLeft, ArrowRight, Loader2, X } from "@/shared/ui/icons";

import { Button, EnTeteDePanneau, Infobulle, Saisie } from "@/shared/ui";
import type {
  CreatorOnboardingState,
  CreatorOnboardingStepName,
} from "@/panels/creators/useCreatorOnboardingState";
import { wizardPanelMountAtom } from "@/panels/creators/atoms";

export type WizardStep = Exclude<CreatorOnboardingStepName, "complete">;

export const WIZARD_STEPS: ReadonlyArray<WizardStep> = [
  "identity",
  "telegram",
  "telegram_group",
] as const;

interface WizardChromeProps {
  state: CreatorOnboardingState | null;
  /** The step the user is currently viewing (may be ahead of state.current_step
   *  if the user used Previous to go back to a done step). */
  viewedStep: WizardStep;
  creatorDisplayName: string | null;
  /** Step content. Rendered in the middle area in `solid` mode (default),
   *  ignored in `slim` mode (the cockpit panel system shows through). */
  children?: ReactNode;
  /** Disabled if the current step is not yet done. */
  canGoNext: boolean;
  /** Disabled on the first step. */
  canGoPrevious: boolean;
  onPrevious: () => void;
  onNext: () => void;
  nextBusy?: boolean;
  /** Label override for the Next button (defaults to "Suivant"). */
  nextLabel?: string;
  /** "slim" : header + footer fixes seuls au-dessus du cockpit (le cockpit
   *  est visible et interactif dans la zone middle, perfect pour les steps
   *  content/enrichment qui utilisent le vrai panel system Vault).
   *  "solid" (defaut) : chrome plein ecran qui rend ses children dans le
   *  middle (les autres steps avec form custom). */
  mode?: "slim" | "solid";
  /** Mode slim uniquement : petit bandeau sticky rendu juste sous le header,
   *  indique a l'user CE qui bloque le passage a l'etape suivante (ex:
   *  "Enrichis: 8/12 • Free: 3/5 • Paid: 2/5"). Sans ce hint, le user voit
   *  le vault vide (apres filtre) et le bouton Next grise, sans savoir
   *  pourquoi il est bloque. */
  slimStatus?: ReactNode;
  /** Si fourni, affiche une croix de fermeture dans le header. Le caller
   *  decide quand permettre la fermeture — typiquement seulement quand on
   *  est en mode "no-creator" (l'user a cliqué "Add Creator" par erreur).
   *  En "in-progress" on garde le wizard force pour ne pas laisser de
   *  creator incomplet en DB sans rappel. */
  onClose?: () => void;
}

export function WizardChrome({
  state,
  viewedStep,
  creatorDisplayName,
  children,
  canGoNext,
  canGoPrevious,
  onPrevious,
  onNext,
  nextBusy,
  nextLabel,
  mode = "solid",
  slimStatus,
  onClose,
}: WizardChromeProps) {
  const { t } = useTranslation();
  const currentIdx = WIZARD_STEPS.indexOf(viewedStep);
  // Le wizard se portail-rend dans `wizardPanelMountAtom` (le body du panneau
  // Créateurs). Le parent (`CreatorOnboardingWizard`) garantit que ce mount
  // existe avant de rendre WizardChrome (auto-ouverture du panneau si besoin).
  // Le fallback shell-level historique est conservé pour les rendus mobile
  // qui shareraient le même chrome.
  const panelMount = useAtomValue(wizardPanelMountAtom);

  const header = (
    <div className="flex shrink-0 flex-col gap-1 border-b border-fg/10 bg-panel pb-3">
      <EnTeteDePanneau
        niveau="h2"
        titre={
          creatorDisplayName
            ? t("shell.creatorOnboardingWizard.titleWithName", {
                name: creatorDisplayName,
              })
            : t("shell.creatorOnboardingWizard.titleEmpty")
        }
        actions={
          <>
            {onClose && (
              <Infobulle texte={t("shell.creatorOnboardingWizard.close")} cote="bas">
                <Button
                  variant="ghost"
                  size="lg"
                  iconOnly
                  onClick={onClose}
                  aria-label={t("shell.creatorOnboardingWizard.close")}
                >
                  <X className="h-4 w-4" />
                </Button>
              </Infobulle>
            )}
          </>
        }
        className="border-b-0 pb-1"
      />
      <div className="px-4">
        <Saisie.Etapes
          etapes={WIZARD_STEPS.map((step) => ({
            cle: step,
            etiquette: t(`shell.creatorOnboardingWizard.steps.${step}.short`),
          }))}
          courante={currentIdx}
          faites={WIZARD_STEPS.filter((step) => state?.[step].done)}
          resume={(numero, total) =>
            t("shell.creatorOnboardingWizard.stepCounter", { current: numero, total })
          }
          ariaLabel={t("shell.creatorOnboardingWizard.ariaLabel")}
        />
      </div>
    </div>
  );

  const footer = (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-fg/10 bg-panel px-4 py-3 sm:px-6">
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="lg"
          onClick={onPrevious}
          disabled={!canGoPrevious}
          icon={<ArrowLeft className="h-3.5 w-3.5" />}
        >
          {t("shell.creatorOnboardingWizard.previous")}
        </Button>
        {onClose && (
          <Button variant="ghost" size="lg" onClick={onClose}>
            {t("shell.creatorOnboardingWizard.cancel")}
          </Button>
        )}
      </div>
      <Button
        variant="accent"
        size="lg"
        onClick={onNext}
        disabled={!canGoNext || nextBusy}
        icon={
          nextBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ArrowRight className="h-3.5 w-3.5" />
          )
        }
      >
        {nextLabel ?? t("shell.creatorOnboardingWizard.next")}
      </Button>
    </div>
  );

  if (mode === "slim") {
    // Slim : juste header sticky en haut + footer sticky en bas, milieu
    // transparent. Le cockpit (avec le panel system + Vault force par
    // wizardLayoutOverrideAtom) est visible et interactif au milieu.
    // `pointer-events-none` sur le wrapper pour que les clics middle
    // passent au cockpit, puis on re-active sur header/footer.
    // children sont rendus en `hidden` (display:none) pour que la step
    // garde son onValidityChange handle actif sans occuper de place.
    return (
      <div
        role="region"
        aria-label={t("shell.creatorOnboardingWizard.ariaLabel")}
        className="pointer-events-none absolute inset-0 z-[150] flex flex-col"
      >
        <div className="pointer-events-auto">{header}</div>
        {slimStatus && (
          <div className="pointer-events-auto border-b border-fg/10 bg-panel/95 px-6 py-2 backdrop-blur-sm">
            {slimStatus}
          </div>
        )}
        <div className="flex-1" />
        <div className="pointer-events-auto">{footer}</div>
        <div className="hidden" aria-hidden>{children}</div>
      </div>
    );
  }

  // Solid mode : si le CreatorsPanel est mounte dans le workspace courant,
  // on portail-render le wizard DEDANS (z plus bas, absolute scope par le
  // .relative du panneau). Sinon on retombe sur le rendu shell-level
  // historique (absolute inset-0 du shell), pour preserver le "force
  // onboarding" quand l'user n'a pas Creators dans son workspace.
  const solidContent = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("shell.creatorOnboardingWizard.ariaLabel")}
      className={
        panelMount
          ? // Portail dans CreatorsPanel : z-50 suffit (au-dessus du contenu
            // panneau sans pour autant ecraser les modales globales z-[100]).
            "absolute inset-0 z-50 flex flex-col bg-canvas text-fg"
          : // `absolute` (pas `fixed`) pour rester confine a la zone shell — la
            // TitleBar et la BottomBar du frame applicatif (cf App.GatedShell)
            // restent visibles dessus.
            "absolute inset-0 z-[200] flex flex-col bg-canvas text-fg"
      }
    >
      {header}
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      {footer}
    </div>
  );
  if (panelMount) return createPortal(solidContent, panelMount);
  return solidContent;
}
